import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../components/ui/simple-toast';
import { 
  Building2, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Package,
  ArrowRightLeft,
  ArrowRight,
  ArrowLeft,
  Receipt,
  Calendar,
  RefreshCw,
  ChevronDown,
  Crown,
  MapPin,
  BarChart3,
  PieChart
} from 'lucide-react';

const BranchReports = () => {
  const { api } = useAuth();
  const { branches, branchId, branchName, isMatriz } = useBranch();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchSummary, setBranchSummary] = useState(null);
  const [consolidated, setConsolidated] = useState(null);
  const [transfersDetail, setTransfersDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('branch'); // branch, consolidated, transfers

  useEffect(() => {
    if (branchId) {
      setSelectedBranch(branchId);
    }
  }, [branchId]);

  useEffect(() => {
    if (selectedBranch) {
      fetchData();
    }
  }, [selectedBranch, period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, consolidatedRes, transfersRes] = await Promise.all([
        api.get(`/reports/branches/summary/${selectedBranch}?period=${period}`),
        api.get(`/reports/branches/consolidated?period=${period}`),
        api.get(`/reports/branches/transfers-detail?period=${period}`)
      ]);
      setBranchSummary(summaryRes.data);
      setConsolidated(consolidatedRes.data);
      setTransfersDetail(transfersRes.data);
    } catch (error) {
      console.error('Error fetching branch reports:', error);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const periodOptions = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Última Semana' },
    { value: 'month', label: 'Último Mes' },
    { value: 'year', label: 'Último Año' }
  ];

  return (
    <>
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-chivo font-extrabold flex items-center gap-2">
                <BarChart3 className="w-8 h-8 text-accent" />
                Reportes por Sucursal
              </h1>
              <p className="text-muted-foreground">
                Análisis financiero detallado por sucursal y consolidado
              </p>
            </div>
            
            <div className="flex gap-2 items-center">
              {/* Period Selector */}
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                {periodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              
              <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b pb-2">
            <Button
              type="button"
              variant={activeTab === 'branch' ? 'default' : 'ghost'}
              onClick={(e) => { e.preventDefault(); setActiveTab('branch'); }}
              className={activeTab === 'branch' ? 'bg-accent text-white' : ''}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Por Sucursal
            </Button>
            <Button
              type="button"
              variant={activeTab === 'consolidated' ? 'default' : 'ghost'}
              onClick={(e) => { e.preventDefault(); setActiveTab('consolidated'); }}
              className={activeTab === 'consolidated' ? 'bg-accent text-white' : ''}
            >
              <PieChart className="w-4 h-4 mr-2" />
              Consolidado
            </Button>
            <Button
              type="button"
              variant={activeTab === 'transfers' ? 'default' : 'ghost'}
              onClick={(e) => { e.preventDefault(); setActiveTab('transfers'); }}
              className={activeTab === 'transfers' ? 'bg-accent text-white' : ''}
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Traspasos
            </Button>
          </div>

          {/* Branch Selector (only for branch tab) */}
          {activeTab === 'branch' && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Seleccionar Sucursal:</label>
              <div className="flex flex-wrap gap-2">
                {branches.map(branch => (
                  <Button
                    type="button"
                    key={branch.branch_id}
                    variant={selectedBranch === branch.branch_id ? 'default' : 'outline'}
                    onClick={(e) => { e.preventDefault(); setSelectedBranch(branch.branch_id); }}
                    className={selectedBranch === branch.branch_id ? 'bg-accent text-white' : ''}
                  >
                    {branch.is_matriz ? <Crown className="w-4 h-4 mr-1" /> : <MapPin className="w-4 h-4 mr-1" />}
                    {branch.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-accent" />
              <p className="mt-2 text-muted-foreground">Cargando reportes...</p>
            </div>
          ) : (
            <>
              {/* Branch Summary Tab */}
              {activeTab === 'branch' && branchSummary && (
                <div className="space-y-6">
                  {/* Branch Header */}
                  <div className={`p-4 rounded-lg ${branchSummary.branch.is_matriz ? 'bg-accent/10 border-2 border-accent' : 'bg-blue-50 border-2 border-blue-300'}`}>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      {branchSummary.branch.is_matriz ? <Crown className="w-5 h-5 text-accent" /> : <MapPin className="w-5 h-5 text-blue-600" />}
                      {branchSummary.branch.name}
                      {branchSummary.branch.is_matriz && <span className="text-sm font-normal text-accent">(Principal)</span>}
                    </h2>
                  </div>

                  {/* KPIs Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Sales */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <DollarSign className="w-4 h-4" />
                          Ventas
                        </div>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(branchSummary.sales.total)}</p>
                        <p className="text-xs text-muted-foreground">{branchSummary.sales.orders_count} órdenes</p>
                      </CardContent>
                    </Card>

                    {/* Purchases (only Matriz) */}
                    {branchSummary.branch.is_matriz && (
                      <Card>
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            <ShoppingCart className="w-4 h-4" />
                            Compras
                          </div>
                          <p className="text-2xl font-bold text-red-600">{formatCurrency(branchSummary.purchases.total)}</p>
                          <p className="text-xs text-muted-foreground">{branchSummary.purchases.count} compras</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Transfers In (only branches) */}
                    {!branchSummary.branch.is_matriz && (
                      <Card className="bg-blue-50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 text-blue-600 text-sm">
                            <ArrowLeft className="w-4 h-4" />
                            Recibido de Matriz
                          </div>
                          <p className="text-2xl font-bold text-blue-600">{formatCurrency(branchSummary.transfers_in.total)}</p>
                          <p className="text-xs text-muted-foreground">{branchSummary.transfers_in.count} traspasos</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Transfers Out */}
                    {branchSummary.branch.is_matriz && (
                      <Card className="bg-orange-50">
                        <CardContent className="pt-4">
                          <div className="flex items-center gap-2 text-orange-600 text-sm">
                            <ArrowRight className="w-4 h-4" />
                            Traspasos a Sucursales
                          </div>
                          <p className="text-2xl font-bold text-orange-600">{formatCurrency(branchSummary.transfers_out.total)}</p>
                          <p className="text-xs text-muted-foreground">{branchSummary.transfers_out.count} traspasos</p>
                        </CardContent>
                      </Card>
                    )}

                    {/* Profit */}
                    <Card className={branchSummary.profit.gross >= 0 ? 'bg-green-50' : 'bg-red-50'}>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <TrendingUp className="w-4 h-4" />
                          Ganancia Bruta
                        </div>
                        <p className={`text-2xl font-bold ${branchSummary.profit.gross >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(branchSummary.profit.gross)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Costo venta: {formatCurrency(branchSummary.sales.cost_of_sales)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Transfers Detail (for Matriz) */}
                  {branchSummary.branch.is_matriz && branchSummary.transfers_out.by_destination.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <ArrowRightLeft className="w-5 h-5" />
                          Traspasos a Sucursales
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {branchSummary.transfers_out.by_destination.map((dest, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                <span className="font-medium">{dest.branch_name}</span>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-orange-600">{formatCurrency(dest.total)}</p>
                                <p className="text-xs text-muted-foreground">{dest.count} traspasos</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Consolidated Tab */}
              {activeTab === 'consolidated' && consolidated && (
                <div className="space-y-6">
                  {/* Total KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-green-700 text-sm">
                          <DollarSign className="w-4 h-4" />
                          Ventas Totales
                        </div>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(consolidated.totals.sales)}</p>
                        <p className="text-xs text-muted-foreground">{consolidated.totals.orders} órdenes</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-red-50 border-red-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-red-700 text-sm">
                          <ShoppingCart className="w-4 h-4" />
                          Compras Totales
                        </div>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(consolidated.totals.purchases)}</p>
                        <p className="text-xs text-muted-foreground">Desde Matriz</p>
                      </CardContent>
                    </Card>

                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-purple-700 text-sm">
                          <Receipt className="w-4 h-4" />
                          Costo de Venta
                        </div>
                        <p className="text-2xl font-bold text-purple-700">{formatCurrency(consolidated.totals.cost_of_sales)}</p>
                      </CardContent>
                    </Card>

                    <Card className={`${consolidated.totals.gross_profit >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="w-4 h-4" />
                          Ganancia Bruta
                        </div>
                        <p className={`text-2xl font-bold ${consolidated.totals.gross_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(consolidated.totals.gross_profit)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Margen: {formatPercent(consolidated.totals.profit_margin)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* By Branch Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Desglose por Sucursal</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left p-3">Sucursal</th>
                              <th className="text-right p-3">Ventas</th>
                              <th className="text-right p-3">Órdenes</th>
                              <th className="text-right p-3">Costo Venta</th>
                              <th className="text-right p-3">Ganancia</th>
                              <th className="text-right p-3">Margen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {consolidated.by_branch.map((branch, idx) => (
                              <tr key={idx} className="border-b hover:bg-muted/30">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    {branch.is_matriz ? (
                                      <Crown className="w-4 h-4 text-accent" />
                                    ) : (
                                      <MapPin className="w-4 h-4 text-blue-600" />
                                    )}
                                    <span className="font-medium">{branch.name}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-right font-medium text-green-600">
                                  {formatCurrency(branch.sales)}
                                </td>
                                <td className="p-3 text-right">{branch.orders}</td>
                                <td className="p-3 text-right text-muted-foreground">
                                  {formatCurrency(branch.cost_of_sales)}
                                </td>
                                <td className={`p-3 text-right font-bold ${branch.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(branch.gross_profit)}
                                </td>
                                <td className="p-3 text-right">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    branch.profit_margin >= 30 ? 'bg-green-100 text-green-700' :
                                    branch.profit_margin >= 15 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {formatPercent(branch.profit_margin)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-muted/50 font-bold">
                              <td className="p-3">TOTAL</td>
                              <td className="p-3 text-right text-green-600">{formatCurrency(consolidated.totals.sales)}</td>
                              <td className="p-3 text-right">{consolidated.totals.orders}</td>
                              <td className="p-3 text-right">{formatCurrency(consolidated.totals.cost_of_sales)}</td>
                              <td className={`p-3 text-right ${consolidated.totals.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(consolidated.totals.gross_profit)}
                              </td>
                              <td className="p-3 text-right">{formatPercent(consolidated.totals.profit_margin)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Transfers Tab */}
              {activeTab === 'transfers' && transfersDetail && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-orange-50">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-orange-700 text-sm">
                          <ArrowRightLeft className="w-4 h-4" />
                          Total Traspasos
                        </div>
                        <p className="text-2xl font-bold text-orange-700">{transfersDetail.total_transfers}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2 text-blue-700 text-sm">
                          <DollarSign className="w-4 h-4" />
                          Valor Total
                        </div>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(transfersDetail.total_value)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Routes */}
                  {transfersDetail.routes.map((route, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-base">
                          <span className="flex items-center gap-1 px-2 py-1 bg-accent/10 rounded text-accent">
                            <Crown className="w-4 h-4" />
                            {route.from_branch.name}
                          </span>
                          <ArrowRight className="w-5 h-5 text-muted-foreground" />
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded text-blue-700">
                            <MapPin className="w-4 h-4" />
                            {route.to_branch.name}
                          </span>
                          <span className="ml-auto font-bold text-orange-600">
                            {formatCurrency(route.total_value)}
                          </span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-2">{route.transfers_count} traspasos</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {route.supplies.slice(0, 8).map((supply, sidx) => (
                            <div key={sidx} className="p-2 bg-muted/30 rounded text-xs">
                              <p className="font-medium truncate">{supply.name}</p>
                              <p className="text-muted-foreground">{supply.quantity} {supply.unit}</p>
                              <p className="font-bold text-accent">{formatCurrency(supply.value)}</p>
                            </div>
                          ))}
                        </div>
                        {route.supplies.length > 8 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            +{route.supplies.length - 8} insumos más
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}

                  {transfersDetail.routes.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay traspasos en el período seleccionado</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default BranchReports;
